<template>
    <div
        v-gtm:type="'Prebuilt'"
        v-gtm:label="order"
        v-gtm:@view
        class="card notranslate"
        v-bind:class="{
            live: event.isLive,
            toggle: isShowExpandToggle,
            expand: isExpanded,
        }"
    >
        <div class="card_content">
            <BetBuilderSelectionBlock
                v-bind:selections="displayedSelections"
                v-bind:event="event"
                v-bind:isShowExpandToggle="isShowExpandToggle"
                v-bind:isExpanded="isExpanded"
                v-on:toggleExpand="toggleExpand"
            />
        </div>
        <div class="card_footer">
            <span>{{ maxOddsLabel }}:</span>
            <strong class="oddsPrice" v-bind:class="{ 'odds-change': isOddsChanged }">
                @ {{ maxOddsDisplay }}
            </strong>
            <VButton
                v-gtm:@click
                color="primary"
                hoverColor="primary"
                textColor="white"
                padding="var(--spacers-md) var(--spacers-xl)"
                v-on:click="addToBetSlip"
            >
                {{ addToSlipLabel }}
            </VButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import xorBy from 'lodash/xorBy';
    import { storeToRefs } from 'pinia';
    import { computed, ref, toRefs, watch } from 'vue';
    import BetBuilderSelectionBlock from '@/components/betBuilderRecommendation/BetBuilderSelectionBlock.vue';
    import VButton from '@/components/common/button/VButton.vue';
    import { useBetBuilderPreBuiltConnection } from '@/composable/useBetBuilderPreBuiltConnection';
    import { i18n } from '@/core/lib/i18n';
    import { formatMinDecimalPlace } from '@/core/lib/numberFormat';
    import type { IEvent } from '@/interface/IEvent';
    import type { IBetBuilderCombination, IBetBuilderTicket } from '@/interface/betBuilder';
    import { AsiaBetPageType, TabName } from '@/interface/enum';
    import { useBetSlipStore } from '@/store/betSlipStore';
    import { useLayoutStore } from '@/store/layoutStore';

    const MAX_VISIBLE_SELECTIONS = 2;
    const ODDS_CHANGED_DURATION_MS = 3000;

    const props = defineProps<{
        combination: IBetBuilderCombination;
        event: IEvent;
        order: number;
    }>();

    const { combination } = toRefs(props);

    const emit = defineEmits<{
        deleted: [combinationKey: string];
    }>();

    const pushedMaxOdds = ref<number | null>(null);
    const realtimeMaxOdds = computed(() => pushedMaxOdds.value ?? combination.value.maxOdds);

    useBetBuilderPreBuiltConnection(
        combination.value.combinationKey,
        (data) => {
            pushedMaxOdds.value = data.maxOdds;
        },
        () => {
            emit('deleted', combination.value.combinationKey);
        },
    );

    const maxOddsDisplay = computed(() => formatMinDecimalPlace(realtimeMaxOdds.value, 2));

    const isOddsChanged = ref(false);
    watch(maxOddsDisplay, () => {
        isOddsChanged.value = true;
        setTimeout(() => {
            isOddsChanged.value = false;
        }, ODDS_CHANGED_DURATION_MS);
    });

    const maxOddsLabel = computed(() => i18n.t('popular_combination_max_odds'));
    const addToSlipLabel = computed(() => i18n.t('popular_combination_button_add_to_slip'));

    const isExpanded = ref(false);

    const isShowExpandToggle = computed(() => combination.value.selections.length > MAX_VISIBLE_SELECTIONS);

    const displayedSelections = computed(() => (isExpanded.value
        ? combination.value.selections
        : combination.value.selections.slice(0, MAX_VISIBLE_SELECTIONS)));

    function toggleExpand() {
        isExpanded.value = !isExpanded.value;
    }

    const { removeBetBuilderBetSlip, setBetBuilderBetSlipTickets } = useBetSlipStore();
    const { betBuilderBetSlip } = storeToRefs(useBetSlipStore());
    const { setBetSlipActiveTab, toggleIsSportListExpanded, toggleBetSlipExpanded, toggleMyBetsExpanded } = useLayoutStore();
    const { isLayoutExpanded } = storeToRefs(useLayoutStore());

    const tickets = computed<IBetBuilderTicket[]>(() => combination.value.selections.map(selection => ({
        key: selection.id.toString(),
        match: props.event,
        selection,
        sportType: props.event.sportType,
        market: selection.market,
        betPage: AsiaBetPageType.BetBuilderRecommendation,
    })));

    function addToBetSlip() {
        const difference = xorBy(betBuilderBetSlip.value?.tickets ?? [], tickets.value, x => x.key);
        if (difference.length > 0) {
            removeBetBuilderBetSlip();
            setBetBuilderBetSlipTickets(tickets.value);
        }

        setBetSlipActiveTab(TabName.Single);
        toggleIsSportListExpanded(false);
        toggleBetSlipExpanded(true);
        if (!isLayoutExpanded.value) toggleMyBetsExpanded(false);
    }
</script>

<style lang="scss" scoped>
    @import "@/components/mpRecommendation/MpCombination";
    @import "@/components/betBuilderRecommendation/BetBuilderRecommendation";

    .card.toggle:not(.expand) {
        :deep(.subBet:last-child::before) {
            height: 100%;
        }
    }
</style>
